import React, {useState} from 'react';
import { LeftSidebar as NavFrame } from "layout-blueprints";
import StandardHeader from "components/global/StandardHeader";
import styled from "styled-components";
import {AppBar, Box, Button, Divider as MuiDivider, Tab, Tabs, Typography, Modal, Card, CardContent, IconButton} from "@mui/material";
import {spacing} from "@mui/system";
import {Add as AddIcon} from "@mui/icons-material";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ConstructionIcon from '@mui/icons-material/Construction';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DisconnectScheduleTable from "modules/disconnectSchedules/components/Table";
import DisconnectScheduleForm from "modules/disconnectSchedules/components/Form";
import CSVUpload from "modules/disconnectSchedules/components/CSVUpload";
import ConfirmDeleteDialog from "modules/disconnectSchedules/components/ConfirmDeleteDialog";
import DisconnectHoldTable from "modules/disconnectHolds/components/Table";
import DisconnectHoldForm from "modules/disconnectHolds/components/Form";
import CalendarView from "modules/disconnectSchedules/components/CalendarView";
import { connect } from "react-redux";
import { post, put, deleteDisconnectSchedule, get, postBatch } from "modules/disconnectSchedules/api/actions";
import { get as getHolds, liftDisconnectHold, post as postHold, put as putHold, deleteDisconnectHold } from "modules/disconnectHolds/api/actions";
import PropTypes from "prop-types";

const Divider = styled(MuiDivider)(spacing);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tabpanel-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Index = (props) => {
  const [value, setValue] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [holdFormOpen, setHoldFormOpen] = useState(false);
  const [editingHold, setEditingHold] = useState(null);
  const [isLifting, setIsLifting] = useState(false);
  const [isLiftMode, setIsLiftMode] = useState(false);
  const [holdDeleteDialogOpen, setHoldDeleteDialogOpen] = useState(false);
  const [holdToDelete, setHoldToDelete] = useState(null);
  const [isDeletingHold, setIsDeletingHold] = useState(false);
  
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleNewSchedule = () => {
    setEditingSchedule(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingSchedule(null);
  };

  const handleCsvUpload = () => {
    setCsvUploadOpen(true);
  };

  const handleCloseCsvUpload = () => {
    setCsvUploadOpen(false);
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setFormOpen(true);
  };

  const handleDeleteSchedule = (schedule) => {
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!scheduleToDelete) return;
    
    setIsDeleting(true);
    try {
      await props.deleteDisconnectSchedule(scheduleToDelete.uuid);
      props.get();
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
    } catch (error) {
      console.error("Error deleting disconnect schedule:", error);
      // TODO: Show error snackbar
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setScheduleToDelete(null);
  };

  const handleNewHold = () => {
    setEditingHold(null);
    setIsLiftMode(false);
    setHoldFormOpen(true);
  };

  const handleCloseHoldForm = () => {
    setHoldFormOpen(false);
    setEditingHold(null);
    setIsLiftMode(false);
  };

  const handleLiftHold = (hold) => {
    setEditingHold(hold);
    setIsLiftMode(true);
    setHoldFormOpen(true);
  };

  const handleConfirmLift = async (hold, liftReason) => {
    setIsLifting(true);
    try {
      await props.liftDisconnectHold(hold.uuid, liftReason);
      handleCloseHoldForm();
      props.getHolds();
    } catch (error) {
      console.error("Error lifting disconnect hold:", error);
      // TODO: Show error snackbar
    } finally {
      setIsLifting(false);
    }
  };

  const handleEditHold = (hold) => {
    setEditingHold(hold);
    setIsLiftMode(false);
    setHoldFormOpen(true);
  };

  const handleSaveHold = async (holdData, isUpdate, uuid) => {
    try {
      if (isUpdate) {
        await props.putHold(holdData, uuid);
      } else {
        await props.postHold(holdData);
      }
      handleCloseHoldForm();
      props.getHolds();
    } catch (error) {
      console.error("Error saving disconnect hold:", error);
      // TODO: Show error snackbar
    }
  };

  const handleSaveSchedule = async (scheduleData, isUpdate) => {
    try {
      if (isUpdate) {
        await props.put(scheduleData);
      } else {
        await props.post(scheduleData);
      }
      handleCloseForm();
      props.get(); // Refresh the list
    } catch (error) {
      console.error("Error saving disconnect schedule:", error);
      // TODO: Show error snackbar
    }
  };


  const handleDeleteHold = (hold) => {
    setHoldToDelete(hold);
    setHoldDeleteDialogOpen(true);
  };

  const handleConfirmDeleteHold = async () => {
    if (!holdToDelete) return;
    
    setIsDeletingHold(true);
    try {
      await props.deleteDisconnectHold(holdToDelete.uuid);
      props.getHolds();
      setHoldDeleteDialogOpen(false);
      setHoldToDelete(null);
    } catch (error) {
      console.log("Error deleting disconnect hold:", error);
      // TODO: Show error snackbar
    } finally {
      setIsDeletingHold(false);
    }
  };

  const handleCloseHoldDeleteDialog = () => {
    setHoldDeleteDialogOpen(false);
    setHoldToDelete(null);
  };

  return (
    <NavFrame page={"Disconnect Schedules"}>
      <StandardHeader title="Disconnect Schedules Management" />
      <Divider className="mb-3 mt-3" />
      <Box className="mb-3" display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewSchedule}
        >
          New Schedule
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudUploadIcon />}
          onClick={handleCsvUpload}
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          Upload Schedules
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<ErrorOutlineIcon />}
          onClick={handleNewHold}
        >
          New Hold
        </Button>
      </Box>
      <AppBar position="static" color="primary">
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="Disconnect Schedules Management Tabs"
        >
          <Tab label="Disconnect Schedules" {...a11yProps(0)} />
          <Tab label="Disconnect Holds" {...a11yProps(1)} />
          <Tab label="Calendar View" {...a11yProps(2)} />
        </Tabs>
      </AppBar>
      <TabPanel value={value} index={0}>
        <DisconnectScheduleTable 
          onEditSchedule={handleEditSchedule}
          onDeleteSchedule={handleDeleteSchedule}
        />
      </TabPanel>

      <TabPanel value={value} index={1}>
        <DisconnectHoldTable 
          onEditHold={handleEditHold}
          onDeleteHold={handleDeleteHold}
          onLiftHold={handleLiftHold}
        />
      </TabPanel>

      <TabPanel value={value} index={2}>
        <CalendarView />
      </TabPanel>

      <DisconnectScheduleForm
        open={formOpen}
        onClose={handleCloseForm}
        onSave={handleSaveSchedule}
        editingSchedule={editingSchedule}
        isLoading={props.disconnectSchedules?.isLoading || false}
      />

      <CSVUpload
        open={csvUploadOpen}
        onClose={handleCloseCsvUpload}
        postBatch={props.postBatch}
        get={props.get}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        schedule={scheduleToDelete}
        isLoading={isDeleting}
      />

      <ConfirmDeleteDialog
        open={holdDeleteDialogOpen}
        onClose={handleCloseHoldDeleteDialog}
        onConfirm={handleConfirmDeleteHold}
        schedule={holdToDelete}
        isLoading={isDeletingHold}
        title="Delete Disconnect Hold"
        message={`Are you sure you want to delete this disconnect hold? This action cannot be undone.`}
      />

      <DisconnectHoldForm
        open={holdFormOpen}
        onClose={handleCloseHoldForm}
        onSave={handleSaveHold}
        onLift={handleConfirmLift}
        editingHold={editingHold}
        liftingHold={isLiftMode}
        isLoading={props.disconnectHolds?.isLoading || false}
        disconnectHolds={props.disconnectHolds}
      />

    </NavFrame>
  );
};

Index.propTypes = {
  disconnectSchedules: PropTypes.object.isRequired,
  disconnectHolds: PropTypes.object.isRequired,
  post: PropTypes.func.isRequired,
  put: PropTypes.func.isRequired,
  deleteDisconnectSchedule: PropTypes.func.isRequired,
  get: PropTypes.func.isRequired,
  getHolds: PropTypes.func.isRequired,
  liftDisconnectHold: PropTypes.func.isRequired,
};

function State(state) {
  return {
    disconnectSchedules: state.disconnectSchedules,
    disconnectHolds: state.disconnectHolds,
  };
}

export default connect(State, { 
  post, 
  put, 
  deleteDisconnectSchedule, 
  get, 
  postBatch,
  getHolds, 
  liftDisconnectHold,
  postHold,
  putHold,
  deleteDisconnectHold
})(Index);